import { Stack } from "@mui/material";
import { useCallback } from "react";
import type { FieldConfiguration } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import {
  BiAggregation,
  FieldConfigurationType,
  MeasureDateRange,
} from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ReportField } from "../../../../../../../../../api/types/svTypes.ts";
import { getMeasureRanges } from "../../../utils/dateRangeToString";
import DateRanges from "../measures/DateRanges";
import AggregationType from "./AggregationType";

export interface DimensionAggregationProps {
  field: ReportField;
  onChange: (value: Partial<FieldConfiguration>) => void;
  hideAggregationType?: boolean;
  hideDateRange?: boolean;
  readonly: boolean;
}

const DimensionAggregation = ({
  field,
  onChange,
  hideAggregationType,
  hideDateRange,
  readonly,
}: DimensionAggregationProps) => {
  const isRangeSelectionDisabled = !field.config.aggregation || field.config.aggregation === BiAggregation.None;

  const onChangeAggregation = useCallback(
    (value: BiAggregation | undefined) => {
      const obj: Partial<FieldConfiguration> = { aggregation: value };
      if (!hideDateRange && !field.config.dateRange) {
        obj.dateRange = MeasureDateRange.EndingBalance;
      }
      if (value === BiAggregation.None || !value) {
        obj.type = FieldConfigurationType.Dimension;
      } else {
        obj.type = FieldConfigurationType.Aggregation;
      }
      onChange(obj);
    },
    [field.config.dateRange, hideDateRange, onChange]
  );

  const handleDateRangeChange = useCallback(
    (value: MeasureDateRange) => {
      onChange({ dateRange: value });
    },
    [onChange]
  );

  return (
    <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
      {!hideAggregationType && (
        <AggregationType field={field} onChangeAggregation={onChangeAggregation} readonly={readonly} />
      )}
      {!hideDateRange && !isRangeSelectionDisabled && (
        <DateRanges
          dateRange={field.config.dateRange || MeasureDateRange.EndingBalance}
          ranges={getMeasureRanges()}
          onChange={handleDateRangeChange}
          disabled={readonly}
        />
      )}
    </Stack>
  );
};

export default DimensionAggregation;
